/**
 * English - `aiImages`: the AI Image Studio page and dialog, its history, and
 * the AI credit card, panel and prompts shown around the dashboard.
 *
 * Keys: `studio.*` for the studio page, `dialog.*` for the studio in a dialog,
 * `steps.*` for the step headings both share, `source.*`, `style.*`,
 * `generating.*` and `variations.*` for the wizard's parts - including
 * `variations.choose_count`, the choice of how many to make - `history.*` for
 * the history page, `credits.*` for credit counts and balances, `card.*` and
 * `panel.*` for the dashboard card and billing panel, `not_enough.*` for the
 * shortfall notice, `apply.*` for what "Use image" did, `fit.*` for cropping a
 * result to the shape of the slot it is going to, and `use_image.*` for
 * that button's wording. Enum values are used verbatim: `status.COMPLETED`,
 * `operations.HD_UPGRADE`, `source.kinds.ai_generated`. Creative type, style
 * and variation names come from the server and are not translated here.
 */
const aiImages: Record<string, string> = {
  'studio.title': 'AI Image Studio',
  'studio.subtitle': 'Turn your existing images into professional, store-ready visuals.',
  'studio.load_failed': 'We could not open the AI Image Studio just now. Please refresh the page.',
  'studio.credits_label': 'AI credits',
  'studio.history': 'History',
  'studio.go_back': 'Go back',
  'studio.use_anywhere': 'You can use it anywhere in NeonCarts.',
  'studio.instruction_placeholder': 'For example: show the product in a premium office environment.',
  'studio.cost': 'This generation uses {credits}.',
  'studio.choose_to_continue': 'Choose an image and what you are making to continue.',
  'studio.fit_note': '{destination}: will be fitted to {ratio}.',

  'dialog.title': 'Make a {subject} with AI',
  'dialog.takes_a_while': 'This usually takes a minute or two.',
  'dialog.cost_one': 'Uses {count} AI credit.',
  'dialog.cost_other': 'Uses {count} AI credits.',
  'dialog.choose_to_continue': 'Choose an image to continue.',
  'dialog.load_failed': 'We could not open the AI studio just now.',
  'dialog.pick_intro': 'Pick the one you want. It drops straight into your {subject} - nothing is published until you save your theme.',
  'dialog.use_this': 'Use this',
  'dialog.hd_cost': 'Uses {credits} more',
  'dialog.instruction_placeholder': 'For example: keep plenty of empty space on the left for a headline.',
  'dialog.fit_note': 'Will be fitted to {ratio} for your {subject}.',

  'steps.choose_image': 'Choose your image',
  'steps.what_making': 'What are you making?',
  'steps.choose_style': 'Choose a style',
  'steps.anything_else': 'Anything else?',
  'steps.extra_instructions': 'Extra instructions',

  'actions.generate_one': 'Generate 1 variation',
  'actions.generate_other': 'Generate {count} variations',
  'actions.start_again': 'Start again',
  'actions.try_again': 'Try again',
  'actions.make_with_ai': 'Make with AI',

  'errors.start_failed': 'We could not start that generation.',
  'errors.save_failed': 'We could not save that image.',
  'errors.hd_failed': 'We could not create the high-quality version.',
  'errors.hd_failed_short': 'We could not create the HD version.',
  'errors.use_failed': 'We could not use that image. Please try again.',
  'errors.delete_failed': 'We could not delete that.',
  'errors.upload_failed': 'We could not upload that image.',
  'errors.too_large': 'That image is too large. Pick one under {size} MB.',
  'errors.did_not_finish': 'That generation did not finish.',

  'source.selected_alt': 'The image your variations will be made from',
  'source.ready': 'Ready to work from this image',
  'source.ready_hint': 'Your variations will keep this product, its colours and its branding.',
  'source.choose_different': 'Choose a different image',
  'source.tabs.library': 'Your images',
  'source.choose_image': 'Choose an image',
  'source.or_drop': 'or drop it here',
  'source.formats_hint': '{formats} up to {size} MB.',
  'source.uploading': 'Uploading your image…',
  'source.empty_title': 'No images yet',
  'source.empty_description': 'Upload a photo, or add pictures to your products first.',
  'source.kinds.ai_generated': 'Made with AI',
  'source.kinds.product': 'Product photos',
  'source.kinds.category': 'Category images',
  'source.kinds.blog': 'Blog covers',
  'source.kinds.store': 'Your store',
  'source.kinds.theme': 'Theme banners',
  'source.kinds.stock': 'Stock pictures',
  'source.unsupported': 'That kind of image cannot be used here. Please use {formats}.',

  'fit.title': 'Fit to {ratio}',
  'fit.hint': 'Drag the frame to choose what is kept. The rest is trimmed so it fits its place exactly.',
  'fit.confirm': 'Use this crop',
  'fit.back': 'Back to variations',
  'fit.load_failed': 'We could not open this image to crop it. You can use it as it is, or go back and pick another.',
  'fit.use_as_is': 'Use it as it is',

  'style.heading': 'Style',
  'style.optional_hint': 'Optional - skip it and we will choose for you.',
  'style.none': 'No particular style',
  'style.none_description': 'Let each variation follow its own direction.',
  'style.size': 'Size',

  'generating.title': 'Creating your AI variations…',
  'generating.getting_started': 'Getting started…',
  'generating.steps.analysing': 'Analysing your image',
  'generating.steps.first': 'Creating your first variation',
  'generating.steps.second': 'Creating your second variation',
  'generating.steps.third': 'Creating your third variation',
  'generating.leave_hint': 'This usually takes a minute or two. You can leave this page - your images will be waiting in your history.',

  'variations.title_one': 'Your AI variation',
  'variations.title_other': 'Your AI variations',
  'variations.subtitle_one': 'Use it, or start again. It stays in your history either way.',
  'variations.subtitle_other': 'Pick the one you like. The others stay in your history.',
  'variations.choose_count': 'How many variations?',
  'variations.count_hint': 'Each extra one costs extra credits.',
  'variations.count_one': '{count} variation',
  'variations.count_other': '{count} variations',
  'variations.you_will_get': 'You will get: {labels}.',
  'variations.alt': '{label} variation',
  'variations.enlarge': 'Enlarge the {label} variation',
  'variations.shrink': 'Shrink the {label} variation',
  'variations.download': 'Download the {label} variation',
  'variations.in_use': 'In use',
  'variations.have_hd': 'You already have the high-quality version',
  'variations.hd_cost_one': 'Uses {count} credit',
  'variations.hd_cost_other': 'Uses {count} credits',

  'history.title': 'AI image history',
  'history.subtitle': 'Everything you have created, and what it cost.',
  'history.new_image': 'New image',
  'history.empty_title': 'Nothing generated yet',
  'history.empty_description': 'Upload a photo and the studio will turn it into a professional, store-ready image.',
  'history.empty_action': 'Create your first image',
  'history.credits_returned_one': '{count} credit returned',
  'history.credits_returned_other': '{count} credits returned',
  'history.generate_again': 'Generate again',
  'history.delete_label': 'Delete this generation',
  'history.delete_title': 'Delete from history',
  'history.in_use': 'in use',

  'status.COMPLETED': 'Ready',
  'status.PENDING': 'Starting',
  'status.PROCESSING': 'Creating',
  'status.FAILED': 'Did not finish',
  'status.REFUNDED': 'Credits returned',

  'credits.count_one': '{count} credit',
  'credits.count_other': '{count} credits',
  'credits.ai_count_one': '{count} AI credit',
  'credits.ai_count_other': '{count} AI credits',
  'credits.used_this_month': '{used} used this month',
  'credits.remaining_of': '{remaining} of {limit} remaining',
  'credits.with_bought': '{base} + {bought} bought',
  'credits.unlimited': 'Unlimited',
  'credits.progress_label': 'AI image credits used this month',

  'card.title': 'AI image credits',
  'card.create': 'Create AI image',
  'card.view_usage': 'View usage',

  'panel.open_studio': 'Open the studio',
  'panel.recent_activity': 'Recent activity',

  'operations.PRODUCT': 'Product image',
  'operations.BANNER': 'Store banner',
  'operations.CATEGORY': 'Category image',
  'operations.PROMOTIONAL': 'Promotional creative',
  'operations.CART': 'Cart creative',
  'operations.SOCIAL': 'Social media creative',
  'operations.PROFILE': 'Profile / brand image',
  'operations.HERO': 'Store hero',
  'operations.HD_UPGRADE': 'HD upgrade',

  'not_enough.title': 'Not enough AI credits',
  'not_enough.need_one': 'You need {count} credit for that image.',
  'not_enough.need_other': 'You need {count} credits for that image.',
  'not_enough.have_zero': 'You have none remaining.',
  'not_enough.have_one': 'You have {count} credit remaining.',
  'not_enough.have_other': 'You have {count} credits remaining.',
  'not_enough.buy': 'Buy AI credits',
  'not_enough.view_plans': 'View plans',

  'apply.saved_to_library': 'Saved to your media library.',
  'apply.product': 'Set as the main image for {name}.',
  'apply.category': 'Set as the category image.',
  'apply.store_logo': 'Set as your store logo.',
  'apply.favicon': 'Set as your favicon.',
  'apply.theme': 'Saved to your media library - pick it in the theme customiser.',

  'use_image.default': 'Use image',
  'use_image.product': 'Set as product image',
  'use_image.category': 'Set as category image',
  'use_image.store_logo': 'Set as store logo',
  'use_image.store_banner': 'Set as store banner',
  'use_image.store_hero': 'Set as hero image',
  'use_image.profile': 'Set as profile image',
};

export default aiImages;
